import { CommonModule } from "@angular/common";
import { DateTime } from "luxon";
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  batchTransitionDates,
  hasBatchPredecessor,
} from "../../../../../../../shared/helpers/batch-transition/batch-transition.helper";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { SnackBarService } from "@services/snack-bar.service";
import { TicketManagementService } from "@services/ticket-management.service";
import { ButtonComponent } from "../../../../../../../shared/components/button/button.component";
import { EventDate } from "../../../../../../../shared/components/cards/card-manage-event/types/event-date.type";
import { TicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { DateTimeHelper } from "../../../../../../../shared/helpers/datetime-converter.helper";
import { DialogHeaderComponent } from "../../../../../../../shared/components/dialog-header/dialog-header.component";

import { DatePickerModule } from "primeng/datepicker";
import { first } from "rxjs";
import {
  BatchScheduleProposal,
  BatchScheduleChange,
} from "@services/types/batch-schedule-proposal.type";
import { UpdateTicketBatchRequestBody } from "@services/types/ticket-batch-request.type";
import { TicketBatchResponseBody } from "@services/types/ticket-batch-response.type";
import { DatepickerMaskDirective } from "../../../../../../shared/directives/datepicker-mask/datepicker-mask.directive";
import {
  applyPdvFeeAbsorptionSync,
  bindPdvFeeAbsorptionSync,
  PdvFeeAbsorptionControls,
} from "../../../../../../../shared/helpers/pdv-fee-absorption/pdv-fee-absorption.helper";
import { buildHalfPriceTicket } from "../../../../../../../shared/helpers/half-price/half-price.helper";
import { batchHasPaidSales } from "../../../../../../../shared/helpers/ticket-sales-lock.helper";
import {
  BatchEditModalForm,
  buildBatchEditModalForm,
} from "./batch-edit-modal.form";

type BatchUpdateValues = Required<
  Pick<
    UpdateTicketBatchRequestBody,
    | "sale_start"
    | "sale_end"
    | "transition_mode"
    | "absorbs_platform_fee"
    | "absorbs_processing_fee"
    | "sold_at_pdv"
    | "has_half_price"
  >
>;

export interface BatchEditDialogData {
  ticketBatches: TicketBatch[];
  selectedBatch?: TicketBatch;
  eventDate: EventDate;
  /** Evento gratuito: usa o rótulo "disponibilização" no lugar de "lote". */
  availabilityMode?: boolean;
  onPersisted?: (batches: TicketBatchResponseBody[]) => void;
}

@Component({
  selector: "app-batch-edit-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    DatePickerModule,
    DatepickerMaskDirective,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./batch-edit-modal.component.html",
  styleUrl: "./batch-edit-modal.component.scss",
})
export class BatchEditModalComponent implements OnInit {
  ticketBatches: TicketBatch[];
  selectedBatch?: TicketBatch;
  eventDate: EventDate;
  availabilityMode: boolean;

  batchForm!: BatchEditModalForm;
  isSaving = false;
  proposal?: BatchScheduleProposal;
  saveErrors: string[] = [];
  private pendingSchedule?: {
    payload: UpdateTicketBatchRequestBody;
    values: BatchUpdateValues;
  };
  preserveDuration = false;
  lotType: "advance" | "portaria" = "advance";
  private advancePdvEnabled = false;
  private readonly validCalendarValues: Partial<Record<"startDateTime" | "endDateTime", Date>> = {};

  get scheduleChanges(): BatchScheduleChange[] {
    return this.proposal
      ? [this.proposal.source, ...this.proposal.adjustments]
      : [];
  }

  get eventOpening(): Date | null {
    const value = DateTime.fromISO(
      `${this.eventDate.startDate}T${this.eventDate.startTime}`,
    );
    return value.isValid ? value.toJSDate() : null;
  }

  get salesClosingLimit(): Date | null {
    return this.isPortaria ? this.eventClosing : this.eventOpening;
  }

  get eventClosing(): Date | null {
    const end = DateTime.fromISO(
      `${this.eventDate.endDate}T${this.eventDate.endTime}`,
    );
    return end.isValid ? end.toJSDate() : null;
  }

  get openingLimit(): Date | null {
    const end = this.salesClosingLimit;
    return end ? new Date(end.getTime() - 60000) : null;
  }

  get openingMinimum(): Date | null {
    const opening = this.isPortaria ? this.eventOpening : null;
    if (this.selectedBatch) return opening;
    return opening && opening > this.dateNow ? opening : this.dateNow;
  }

  get closingMinimum(): Date | null {
    const start = this.batchForm?.controls.ticketsDateGroup.controls.startDateTime.value;
    const afterStart = start ? new Date(start.getTime() + 60000) : null;
    if (!this.selectedBatch && (!afterStart || afterStart < this.dateNow)) return this.dateNow;
    return afterStart;
  }

  get isPortaria(): boolean {
    return this.lotType === "portaria";
  }

  get hasPredecessor(): boolean {
    return hasBatchPredecessor(this.ticketBatches, this.selectedBatch);
  }

  get advanceUnavailable(): boolean {
    return !!this.eventOpening && this.eventOpening <= this.dateNow;
  }

  onLotTypeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value === "advance" || value === "portaria") this.selectLotType(value);
  }

  selectLotType(type: "advance" | "portaria"): void {
    if (this.hasPaidSales || this.closedBatch || this.isSaving || this.availabilityMode ||
        (type === "advance" && this.advanceUnavailable)) return;
    if (!this.isPortaria) this.advancePdvEnabled = this.batchForm.controls.soldAtPdv.value;
    this.lotType = type;
    if (this.isPortaria && this.hasPredecessor) {
      // Waiting exhaustion-only successors have no date to encode portaria.
      this.batchForm.controls.transitionMode.setValue("date_or_sold_out", { emitEvent: false });
    }
    this.syncTransitionControls();
    const dates = this.batchForm.controls.ticketsDateGroup.controls;
    const nextMinute = new Date(Math.ceil(Date.now() / 60000) * 60000);
    const currentStart = dates.startDateTime.value;
    const start = this.isPortaria
      ? new Date(Math.max(this.eventOpening?.getTime() ?? nextMinute.getTime(), this.selectedBatch ? -Infinity : nextMinute.getTime()))
      : currentStart && this.eventOpening && currentStart < this.eventOpening && (this.selectedBatch || currentStart >= nextMinute)
        ? currentStart : nextMinute;
    // The type changes the opening interval too. Refresh it before writing
    // a portaria start that was outside the previous advance interval.
    this.changeDetector.detectChanges();
    dates.startDateTime.setValue(start);
    this.syncPdvChannel();
    // Refresh the picker's min/max before writing the new phase's closing.
    this.changeDetector.detectChanges();
    dates.endDateTime.setValue(this.salesClosingLimit);
    if (!this.isPortaria) this.batchForm.controls.soldAtPdv.setValue(this.advancePdvEnabled);
    this.batchForm.controls.ticketsDateGroup.updateValueAndValidity();
    this.batchForm.markAsDirty();
  }

  private syncPdvChannel(): void {
    const control = this.batchForm.controls.soldAtPdv;
    if (this.isPortaria && !this.availabilityMode) {
      control.setValue(true, { emitEvent: false });
      control.disable({ emitEvent: false });
    } else if (!this.hasPaidSales && !this.closedBatch) {
      control.enable({ emitEvent: false });
    }
  }

  private calendarBounds(field: "startDateTime" | "endDateTime"): { min: Date | null; max: Date | null } {
    return field === "startDateTime"
      ? { min: this.openingMinimum, max: this.openingLimit }
      : { min: this.closingMinimum, max: this.salesClosingLimit };
  }

  private calendarValueAllowed(field: "startDateTime" | "endDateTime", value: unknown): boolean {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return false;
    const { min, max } = this.calendarBounds(field);
    return (!min || value >= min) && (!max || value <= max);
  }

  onCalendarSelect(field: "startDateTime" | "endDateTime", value: Date): void {
    if (this.calendarValueAllowed(field, value)) return;
    // PrimeNG's 24-hour spinner can jump from 01h to 11h when decreasing
    // below minDate. Reject the emitted value through the public form API;
    // this also refreshes the picker without changing the dependency.
    const previous = this.validCalendarValues[field];
    const { min, max } = this.calendarBounds(field);
    let restored: Date | null = null;
    if (previous && this.calendarValueAllowed(field, previous)) {
      restored = new Date(previous);
    } else if ((!min || !max || min <= max) && value instanceof Date && !Number.isNaN(value.getTime())) {
      restored = new Date(Math.min(max?.getTime() ?? Infinity, Math.max(min?.getTime() ?? -Infinity, value.getTime())));
    }
    this.batchForm.controls.ticketsDateGroup.controls[field].setValue(restored);
  }

  onCalendarInput(field: "startDateTime" | "endDateTime", event: Event): void {
    const text = (event.target as HTMLInputElement | null)?.value ?? "";
    // Let the user finish typing; reject only a complete out-of-range value.
    if (!/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(text)) return;
    const value = DateTime.fromFormat(text, "dd/MM/yyyy HH:mm").toJSDate();
    this.onCalendarSelect(field, value);
  }

  dateNow = new Date();

  private readonly dialogRef = inject(MatDialogRef<BatchEditModalComponent>);
  private readonly defaultDisableClose = this.dialogRef.disableClose ?? false;
  private readonly formBuilder = inject(FormBuilder);
  private readonly snackbarService = inject(SnackBarService);
  private readonly ticketManagementService = inject(TicketManagementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);

  constructor(@Inject(MAT_DIALOG_DATA) public data: BatchEditDialogData) {
    this.ticketBatches = data.ticketBatches;
    this.selectedBatch = data.selectedBatch;
    this.eventDate = data.eventDate;
    this.availabilityMode = data.availabilityMode ?? false;
  }

  get headerTitle(): string {
    if (this.availabilityMode) {
      return this.selectedBatch !== undefined
        ? "Editar disponibilização"
        : "Definir disponibilização";
    }
    return this.selectedBatch !== undefined ? "Editar Lote" : "Criar Lote";
  }

  get sectionTitle(): string {
    return this.availabilityMode
      ? "Disponibilização dos ingressos"
      : "Lotes de ingressos";
  }

  get sectionDescription(): string {
    return this.availabilityMode
      ? "Defina a data e hora de início e término da disponibilização dos ingressos gratuitos."
      : "Organize suas vendas por lote. Defina a data e hora de início e término para cada lote.";
  }

  ngOnInit() {
    this.batchForm = buildBatchEditModalForm({
      formBuilder: this.formBuilder,
      eventDate: this.eventDate,
      isEditMode: !!this.selectedBatch,
      ticketBatches: this.ticketBatches,
      selectedBatch: this.selectedBatch,
    });

    // Se estiver editando um batch específico, carrega as datas dele
    if (this.selectedBatch) {
      this.patchDatesFromBatch(this.selectedBatch);
    }
    const initialStart = this.batchForm.controls.ticketsDateGroup.controls.startDateTime.value;
    this.lotType = this.selectedBatch?.pdv_only ||
      (initialStart && this.eventOpening && initialStart >= this.eventOpening) ||
      (!this.selectedBatch && this.advanceUnavailable) ? "portaria" : "advance";
    this.advancePdvEnabled = !this.isPortaria && this.batchForm.controls.soldAtPdv.value;
    this.batchForm.controls.ticketsDateGroup.addValidators((group) => {
      if (this.isPortaria && this.hasPredecessor && this.soldOutOnly) return { portariaNeedsScheduledOpening: true };
      const start = group.get("startDateTime")?.value as Date | null;
      const opening = this.eventOpening;
      if (!start || !opening) return null;
      if (this.isPortaria && start < opening) return { portariaOpeningBeforeEventStart: true };
      if (!this.isPortaria && start >= opening) return { advanceOpeningAtOrAfterEventStart: true };
      return null;
    });
    this.syncTransitionControls();
    this.batchForm.controls.transitionMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncTransitionControls());
    if (this.hasPaidSales) {
      for (const name of [
        "transitionMode",
        "absorbsPlatformFee",
        "absorbsProcessingFee",
        "soldAtPdv",
        "hasHalfPrice",
      ] as const) {
        this.batchForm.controls[name].disable({ emitEvent: false });
      }
    } else {
      const pdvFeeSync = bindPdvFeeAbsorptionSync(this.pdvFeeControls);
      this.destroyRef.onDestroy(() => pdvFeeSync.unsubscribe());
    }
    this.syncPdvChannel();
    this.batchForm.controls.ticketsDateGroup.controls.startDateTime.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncPdvChannel());
    for (const field of ["startDateTime", "endDateTime"] as const) {
      const control = this.batchForm.controls.ticketsDateGroup.controls[field];
      const rememberValidValue = (value: Date | null) => {
        if (value && this.calendarValueAllowed(field, value)) {
          this.validCalendarValues[field] = new Date(value);
        }
      };
      rememberValidValue(control.value);
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rememberValidValue);
    }
    this.batchForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveErrors = [];
      });
  }

  get hasPaidSales(): boolean {
    return !!this.selectedBatch && batchHasPaidSales(this.selectedBatch);
  }

  get closedBatch(): boolean {
    return !!this.selectedBatch?.transition_closed_at;
  }

  get soldOutOnly(): boolean {
    return this.batchForm.controls.transitionMode.value === "sold_out_only";
  }

  get openingEditable(): boolean {
    return (
      !this.hasPaidSales &&
      !this.closedBatch &&
      (!this.soldOutOnly ||
        !hasBatchPredecessor(this.ticketBatches, this.selectedBatch))
    );
  }

  private syncTransitionControls(): void {
    if (this.isPortaria && this.hasPredecessor && this.soldOutOnly && !this.hasPaidSales) {
      this.batchForm.controls.transitionMode.setValue("date_or_sold_out", { emitEvent: false });
    }
    const dates = this.batchForm.controls.ticketsDateGroup.controls;
    if (this.openingEditable) dates.startDateTime.enable({ emitEvent: false });
    else if (this.hasPaidSales || this.closedBatch) {
      dates.startDateTime.disable({ emitEvent: false });
    } else {
      const preservedStart = batchTransitionDates(
        "sold_out_only",
        true,
        null,
        null,
        this.selectedBatch,
      ).sale_start;
      dates.startDateTime.setValue(
        preservedStart ? new Date(preservedStart) : null,
        { emitEvent: false },
      );
      dates.startDateTime.disable({ emitEvent: false });
    }
    if (this.soldOutOnly) dates.endDateTime.disable({ emitEvent: false });
    else dates.endDateTime.enable({ emitEvent: false });
    this.batchForm.controls.ticketsDateGroup.updateValueAndValidity();
    if (!this.hasPaidSales)
      applyPdvFeeAbsorptionSync(this.pdvFeeControls, "batchStartDate");
    this.syncPdvChannel();
  }

  private get pdvFeeControls(): PdvFeeAbsorptionControls {
    return {
      ...this.batchForm.controls,
      batchStartDate:
        this.batchForm.controls.ticketsDateGroup.controls.startDateTime,
      eventStartDate: this.eventDate.startDate,
    };
  }

  closeModal() {
    if (this.isSaving) return;
    this.dialogRef.close();
  }

  saveBatch(): void {
    if (this.isSaving || this.closedBatch || this.proposal) return;
    this.batchForm.markAllAsTouched();
    this.batchForm.controls.ticketsDateGroup.updateValueAndValidity();
    this.saveErrors = [];

    const previewOverlap = this.canPreviewOverlap();
    if (!this.batchForm.valid && !previewOverlap) {
      this.snackbarService.openSnackBar(
        "Revise as datas indicadas no formulário.",
        "Fechar",
        "error",
      );
      return;
    }

    const values = this.batchUpdateValues();
    if (this.selectedBatch?.id) {
      this.savePersistedBatch(values, previewOverlap);
      return;
    }
    if (this.selectedBatch) this.applyBatchValues(values);
    else this.ticketBatches.push({ ...values, pdv_only: this.isPortaria, tickets: [] });
    this.finishSave();
  }

  private canPreviewOverlap(): boolean {
    const dates = this.batchForm.controls.ticketsDateGroup;
    return (
      !!this.selectedBatch?.id &&
      dates.hasError("batchDateOverlapsAnotherBatch") &&
      Object.keys(dates.errors ?? {}).length === 1 &&
      Object.values(dates.controls).every(
        (control) => control.disabled || control.valid,
      ) &&
      !this.batchForm.errors
    );
  }

  private batchUpdateValues(): BatchUpdateValues {
    const controls = this.batchForm.controls;
    const dates = controls.ticketsDateGroup.getRawValue();
    const soldAtPdv = this.isPortaria || !!controls.soldAtPdv.value;
    if (!this.hasPaidSales)
      applyPdvFeeAbsorptionSync(this.pdvFeeControls, "soldAtPdv");
    const values: BatchUpdateValues = {
      ...batchTransitionDates(
        controls.transitionMode.value,
        hasBatchPredecessor(this.ticketBatches, this.selectedBatch),
        this.isoDate(dates.startDateTime),
        this.isoDate(dates.endDateTime),
        this.selectedBatch,
      ),
      transition_mode: controls.transitionMode.value,
      absorbs_platform_fee: !!controls.absorbsPlatformFee.value,
      absorbs_processing_fee: !!controls.absorbsProcessingFee.value,
      sold_at_pdv: soldAtPdv,
      has_half_price: !!controls.hasHalfPrice.value,
    };
    // Pickers display minutes; untouched persisted timestamps keep full precision.
    if (this.selectedBatch?.id) {
      values.sale_start = this.preserveUnchangedDate(
        values.sale_start,
        this.selectedBatch.sale_start,
      );
      values.sale_end = this.preserveUnchangedDate(
        values.sale_end,
        this.selectedBatch.sale_end,
      );
    }
    return values;
  }

  private isoDate(value: Date | null): string | null {
    return value
      ? DateTimeHelper.toIsoFormat(
          this.formatDateForIso(value),
          this.formatTimeForIso(value),
        )
      : null;
  }

  private savePersistedBatch(
    values: BatchUpdateValues,
    previewOverlap: boolean,
  ): void {
    const batchId = Number(this.selectedBatch!.id);
    // Paid lots can postpone closing without sending locked fields.
    const payload = this.hasPaidSales ? { sale_end: values.sale_end } : values;
    if (previewOverlap) {
      this.requestScheduleProposal(payload, values);
      return;
    }
    this.setSaving(true);
    this.ticketManagementService
      .updateBatch(batchId, payload)
      .pipe(first())
      .subscribe({
        next: (saved) => {
          const persisted = { ...saved, id: batchId };
          this.applyBatchValues(values);
          this.mergeSavedSchedule(persisted);
          this.data.onPersisted?.([persisted]);
          this.finishSave();
        },
        error: (error) => {
          this.setSaving(false);
          if (error?.error?.schedule_conflict) {
            this.requestScheduleProposal(payload, values);
            return;
          }
          const message = this.updateErrorMessage(error);
          this.saveErrors = [message];
          this.snackbarService.openSnackBar(message, "Fechar", "error");
        },
      });
  }

  private requestScheduleProposal(
    payload: UpdateTicketBatchRequestBody,
    values: BatchUpdateValues,
  ): void {
    this.setSaving(true);
    this.pendingSchedule = { payload, values };
    this.ticketManagementService
      .previewBatchSchedule(
        Number(this.selectedBatch!.id),
        payload,
        this.preserveDuration,
      )
      .pipe(first())
      .subscribe({
        next: (proposal) => {
          this.setSaving(false);
          this.proposal = proposal;
          this.saveErrors = [...proposal.errors];
        },
        error: (error) => this.scheduleError(error),
      });
  }

  changeDurationStrategy(event: Event): void {
    if (this.isSaving || !this.pendingSchedule) return;
    this.preserveDuration = (event.target as HTMLInputElement).checked;
    this.requestScheduleProposal(
      this.pendingSchedule.payload,
      this.pendingSchedule.values,
    );
  }

  private setSaving(saving: boolean): void {
    this.isSaving = saving;
    this.dialogRef.disableClose = saving || this.defaultDisableClose;
  }

  scheduleDuration(dates: BatchScheduleChange["current"]): string {
    if (!dates.sale_start || !dates.sale_end) return "Sem período definido";
    let remaining = Math.max(
      0,
      Math.floor(
        (Date.parse(dates.sale_end) - Date.parse(dates.sale_start)) / 1000,
      ),
    );
    const parts: string[] = [];
    for (const [seconds, name] of [
      [86400, "dia"],
      [3600, "hora"],
      [60, "minuto"],
      [1, "segundo"],
    ] as const) {
      const count = Math.floor(remaining / seconds);
      if (count) parts.push(`${count} ${name}${count === 1 ? "" : "s"}`);
      remaining %= seconds;
    }
    return parts.join(", ") || "Sem tempo de venda";
  }

  backToDates(): void {
    if (this.isSaving) return;
    this.proposal = undefined;
    // Keep typed dates so the producer can fix them without starting over.
    this.saveErrors = [];
  }

  confirmSchedule(): void {
    if (
      this.isSaving ||
      !this.proposal?.token ||
      this.proposal.errors.length ||
      this.proposal.blockers.length
    )
      return;
    this.setSaving(true);
    this.ticketManagementService
      .confirmBatchSchedule(Number(this.selectedBatch!.id), this.proposal.token)
      .pipe(first())
      .subscribe({
        next: (result) => {
          if (this.pendingSchedule)
            this.applyBatchValues(this.pendingSchedule.values);
          const ids = new Set([
            Number(this.selectedBatch!.id),
            ...result.adjusted_ids,
          ]);
          for (const saved of result.batches) {
            if (ids.has(saved.id)) this.mergeSavedSchedule(saved);
          }
          this.data.onPersisted?.(
            result.batches.filter((saved) => ids.has(saved.id)),
          );
          this.dialogRef.disableClose = this.defaultDisableClose;
          this.finishSave();
        },
        error: (error) => {
          // A stale or failed confirmation must be reviewed, never retried silently.
          this.proposal = undefined;
          this.scheduleError(error);
        },
      });
  }

  private scheduleError(error: {
    status?: number;
    error?: { errors?: unknown; error?: unknown };
  }): void {
    this.setSaving(false);
    this.proposal = undefined;
    this.saveErrors = [
      error.status === 403
        ? "Você não tem permissão para editar este lote, ou ele já foi encerrado. Atualize o evento para conferir."
        : this.updateErrorMessage(error),
    ];
  }

  private mergeSavedSchedule(saved: TicketBatchResponseBody): void {
    const draft =
      this.ticketBatches.find((batch) => batch.id === saved.id) ??
      (this.selectedBatch?.id === saved.id ? this.selectedBatch : undefined);
    if (!draft) return;
    // Do not replace unsaved ticket prices, descriptions, stocks or fee drafts.
    for (const name of [
      "sale_start",
      "sale_end",
      "updated_at",
      "on_sale",
      "has_paid_sales",
      "can_transition",
      "transition_closed_at",
      "transition_block_reason",
    ] as const) {
      if (name in saved) Object.assign(draft, { [name]: saved[name] });
    }
    if (saved.id === this.selectedBatch?.id) {
      for (const ticket of saved.tickets ?? []) {
        if (
          !ticket.half_price ||
          draft.tickets.some((item) => item.id === ticket.id)
        )
          continue;
        const paired = draft.tickets.find(
          (item) =>
            !item.id &&
            item.half_price &&
            (item.parent_ticket_id === ticket.parent_ticket_id ||
              item.title === ticket.title),
        );
        if (paired)
          Object.assign(paired, { id: ticket.id, batch_id: ticket.batch_id });
        else
          draft.tickets.push({
            ...ticket,
            price: Number(ticket.price),
          } as TicketBatch["tickets"][number]);
      }
    }
  }

  private preserveUnchangedDate(
    value: string | null,
    original: string | null,
  ): string | null {
    if (
      value &&
      original &&
      Math.floor(Date.parse(value) / 60000) ===
        Math.floor(Date.parse(original) / 60000)
    ) {
      return original;
    }
    return value;
  }

  private updateErrorMessage(error: {
    error?: { errors?: unknown; error?: unknown };
  }): string {
    const details = error?.error?.errors ?? error?.error?.error;
    if (Array.isArray(details))
      return (
        details.filter((item) => typeof item === "string").join(" ") ||
        "Não foi possível salvar as configurações do lote"
      );
    return typeof details === "string"
      ? details
      : "Não foi possível salvar as configurações do lote";
  }

  private applyBatchValues(
    values: Partial<
      Pick<
        TicketBatch,
        | "sale_start"
        | "sale_end"
        | "absorbs_platform_fee"
        | "absorbs_processing_fee"
        | "sold_at_pdv"
        | "has_half_price"
        | "transition_mode"
      >
    >,
  ): void {
    if (!this.selectedBatch) {
      return;
    }

    Object.assign(this.selectedBatch, values);
    this.selectedBatch.pdv_only = this.isPortaria;
    this.ensureHalfPriceTickets(this.selectedBatch);

    if (!this.ticketBatches.includes(this.selectedBatch)) {
      this.ticketBatches.push(this.selectedBatch);
    }
  }

  private ensureHalfPriceTickets(batch: TicketBatch): void {
    if (!batch.has_half_price) return;

    const existing = [...batch.tickets];
    for (const ticket of existing) {
      if (ticket.half_price) continue;
      const alreadyPaired = batch.tickets.some(
        (other) =>
          other.half_price &&
          (other.parent_ticket_id === ticket.id ||
            other.title === `${ticket.title} — Meia-entrada`),
      );
      if (alreadyPaired) continue;
      batch.tickets.push(buildHalfPriceTicket(ticket));
    }
  }

  private finishSave(): void {
    this.setSaving(false);
    this.snackbarService.openSnackBar(
      "Lote salvo com sucesso",
      "Fechar",
      "success",
    );
    this.dialogRef.close(this.ticketBatches);
  }

  private patchDatesFromBatch(batch: TicketBatch) {
    const start = DateTimeHelper.fromIsoFormat(batch.sale_start);
    const end = DateTimeHelper.fromIsoFormat(batch.sale_end);

    const startDateTime = this.buildDateTime(start.date, start.time);
    const endDateTime = this.buildDateTime(end.date, end.time);

    this.batchForm.patchValue({
      ticketsDateGroup: {
        startDateTime,
        endDateTime,
      },
    });
  }

  private buildDateTime(dateValue: string, timeValue: string): Date | null {
    if (!dateValue || !timeValue) return null;

    const isoCandidate = `${dateValue}T${timeValue}`;
    const parsedDate = new Date(isoCandidate);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private formatDateForIso(dateTime: Date): string {
    // YYYY-MM-DD
    const year = dateTime.getFullYear();
    const month = String(dateTime.getMonth() + 1).padStart(2, "0");
    const day = String(dateTime.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private formatTimeForIso(dateTime: Date): string {
    // HH:mm
    const hours = String(dateTime.getHours()).padStart(2, "0");
    const minutes = String(dateTime.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }
}
